import dayjs from "dayjs";
import { VerticalTimeline, VerticalTimelineElement } from "react-vertical-timeline-component";
import "react-vertical-timeline-component/style.min.css";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Card,
  IconButton,
  Spinner,
  Stack,
  Typography,
} from "@altimateai/lego";
import {
  IconBuildingBank,
  IconCircleCheck,
  IconCircleX,
  IconDownload,
  IconHistory,
} from "@tabler/icons-react";
import type { ComboboxOption } from "@altimateai/lego";
import { Paginate } from "@/components/paginate";
import AutoTuneHistoryEventTypeFilter from "./AutoTuneHistoryEventTypeFilter";
import type { AutoTuneHistoryEvent } from "./types";

// Mirror of the warehouse `timeline_contentStyle` / `iconStyle` constants
// (apps/web/src/pages/warehouse/constants.ts) so the timeline renders
// pixel-identically to the warehouse Auto Tune history.
const TIMELINE_CONTENT_STYLE: React.CSSProperties = {
  fontFamily: "Lexend",
  fontStyle: "normal",
  fontWeight: 400,
  fontSize: "12px",
  lineHeight: "15px",
  color: "#082247",
  display: "flex",
  alignItems: "center",
  marginLeft: "240px",
  padding: "12px 14px",
  gap: "10px",
  border: "1px solid #E7E8EA",
  boxShadow: "none",
  borderRadius: "12px",
};

// The library's spine sits at `left: 18px` from the timeline column.
// A 32px circle bisected by that spine needs its left edge at `2px`
// (18 − 16). Use flex centering inside so children land in the middle.
const TIMELINE_ICON_STYLE: React.CSSProperties = {
  width: "32px",
  height: "32px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "white",
  boxShadow: "none",
  border: "1px solid #E7E8EA",
  padding: "0",
  margin: "0",
  top: "8px",
  left: "2px",
  borderRadius: "50%",
};

// VerticalTimelineElement adds a fixed vertical margin between elements
// (~28px each side). Override to a tighter rhythm.
const TIMELINE_ELEMENT_STYLE: React.CSSProperties = {
  margin: "8px 0",
  padding: "0",
};

// Pick the timeline glyph based on the BE event_type × mode pair. The
// labels themselves come from `event_message` which the BE composes
// server-side from ``DBX_AUTO_TUNE_EVENT_UI_MAPPING``.
const iconForEvent = (event: AutoTuneHistoryEvent): typeof IconBuildingBank => {
  if (event.event_type === "FAILED") return IconCircleX;
  if (event.event_type === "APPLIED") {
    return event.mode === "disable" ? IconHistory : IconCircleCheck;
  }
  // REVERTED — a previously enabled recommendation was turned back off
  // (cluster Auto Tune disable). Same revert glyph as APPLIED+disable.
  if (event.event_type === "REVERTED") return IconHistory;
  // APPLY_REQUESTED — workspace decision logged but not yet applied.
  return IconBuildingBank;
};

// The library's stylesheet positions every SVG inside the circle via
// `position: relative; left: 50%; top: 50%; margin: -12px` (assuming
// 24x24). Override those props inline so flex centering on the wrapper
// is the only thing positioning the icon.
const ICON_RESET_STYLE: React.CSSProperties = {
  position: "static",
  left: "auto",
  top: "auto",
  margin: 0,
  width: 18,
  height: 18,
  display: "block",
};

const renderTimelineIcon = (event: AutoTuneHistoryEvent) => {
  const Icon = iconForEvent(event);
  const ts = dayjs(event.change_timestamp).format("hh:mm A, MMM DD, YYYY");
  return (
    <>
      <span
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        <Icon
          size={18}
          stroke={1.75}
          className="al-text-action-orange"
          aria-hidden="true"
          style={ICON_RESET_STYLE}
        />
      </span>
      <Typography
        size="xs"
        className="al-text-stroke-dark al-whitespace-nowrap"
        style={{
          position: "absolute",
          left: "48px",
          top: "50%",
          transform: "translateY(-50%)",
          fontWeight: 400,
        }}
      >
        {ts}
      </Typography>
    </>
  );
};

interface Props {
  events: AutoTuneHistoryEvent[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  onPageChange?: (next: number) => void;
  onDownload?: () => void;
  isLoading?: boolean;
  // Event-type filter options (raw ``event_type`` value + display label).
  eventTypeOptions?: ComboboxOption[];
  // Currently selected raw ``event_type`` values.
  selectedEventTypes?: string[];
  // Fires with the raw ``event_type`` values when the filter is applied.
  onEventTypesChange?: (values: string[]) => void;
}

const AutoTuneHistoryCard = ({
  events,
  total,
  page,
  pageSize,
  totalPages,
  onPageChange,
  onDownload,
  isLoading = false,
  eventTypeOptions,
  selectedEventTypes,
  onEventTypesChange,
}: Props) => (
  <Card className="al-p-4">
    <Stack direction="row" align="center" justify="between" className="al-mb-4">
      <Typography size="lg" weight="medium">
        Auto tune history
      </Typography>
      <Stack direction="row" align="center" gap="sm">
        {eventTypeOptions && onEventTypesChange && (
          <AutoTuneHistoryEventTypeFilter
            options={eventTypeOptions}
            value={selectedEventTypes ?? []}
            onChange={onEventTypesChange}
          />
        )}
        {onDownload && (
          <IconButton
            title="Download history"
            variant="ghost"
            onClick={onDownload}
            aria-label="Download history"
          >
            <IconDownload size={16} />
          </IconButton>
        )}
      </Stack>
    </Stack>

    {isLoading ? (
      <Stack justify="center" align="center" gap="sm" className="al-w-full al-h-80">
        <Spinner />
        <Typography size="sm" className="al-text-stroke-dark">
          Loading events...
        </Typography>
      </Stack>
    ) : total === 0 ? (
      <Stack direction="column" align="center" gap="xs" className="al-py-10">
        <Typography size="lg" weight="medium">
          No Audit Events yet
        </Typography>
        <Typography size="sm" className="al-text-stroke-dark al-text-center al-max-w-md">
          No Decisions found for the selected period. You will see the data once decisions are made.
        </Typography>
      </Stack>
    ) : (
      <>
        <VerticalTimeline animate={false} layout="1-column-left" lineColor="#E7E8EA">
          {events.map(event => {
            // Surface ``actionable_message`` (FAILED) or ``reason`` as
            // the expandable detail; ``event_message`` is always the
            // headline label.
            const detail = event.actionable_message || event.reason || "";
            return (
              <VerticalTimelineElement
                key={event.event_id}
                style={TIMELINE_ELEMENT_STYLE}
                contentStyle={TIMELINE_CONTENT_STYLE}
                contentArrowStyle={{ display: "none" }}
                iconStyle={TIMELINE_ICON_STYLE}
                icon={renderTimelineIcon(event)}
              >
                {detail.length > 0 ? (
                  <Accordion type="single" collapsible className="al-w-full">
                    <AccordionItem className="!al-border-0" value="item-1">
                      <AccordionTrigger className="!al-p-0 al-decoration-0 hover:al-no-underline al-font-normal">
                        {event.event_message}
                      </AccordionTrigger>
                      <AccordionContent className="al-mt-3 al-font-light">
                        {detail}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                ) : (
                  <span>{event.event_message}</span>
                )}
              </VerticalTimelineElement>
            );
          })}
        </VerticalTimeline>
        <Paginate
          itemCount={total}
          page={page - 1}
          pageSize={pageSize}
          numPages={totalPages}
          onPageClick={(next: number) => onPageChange?.(next + 1)}
          onPageSizeChange={() => {}}
        />
      </>
    )}
  </Card>
);

export default AutoTuneHistoryCard;
